import { gql, useMutation, useQuery } from "@apollo/client";
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  LinearProgress,
  Stack,
} from "@mui/material";
import { DateTime } from "luxon";
import { genProductPriceLabel } from "mt-shared/genProductPriceLabel";
import { generateGoogleMapsUrl } from "mt-shared/maps";
import { requiresTransportAgreement } from "mt-shared/transport";
import { useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useNotifications } from "../../../components/Notification";
import { getGrossPrice } from "../../../helpers/get-gross-price";
import { Booking } from "../../../types";
import TransportServiceAgreementModal from "../../Bookings/TransportServiceAgreementModal";
import CheckoutProgress from "../components/CheckoutProgress";
import GuestNamesSummary from "../components/GuestNamesSummary";
import styles from "./index.module.scss";

export default function CheckoutConfirmation() {
  const params = useParams();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const productId = params.id;
  const bookingId = queryParams.get("bookingid");

  if (!productId) return <>404: no product</>;
  if (!bookingId) return <>404: no booking</>;

  return (
    <>
      <Booking id={bookingId} productId={params.id || ""} />
    </>
  );
}

function Booking(props: { id: string; productId: string }) {
  const navigate = useNavigate();
  const { showNotification } = useNotifications();

  const [sendConfirmationEmailToCustomer, setSendConfirmationEmailToCustomer] =
    useState(false);
  const [isTransportAgreementAgreed, setIsTransportAgreementAgreed] =
    useState(false);
  const [tsaModalOpen, setTsaModalOpen] = useState(false);
  const { data, loading, error } = useQuery<{ booking: Booking }>(GET_BOOKING, {
    variables: { id: props.id, lang: "en" },
  });
  const [confirmBooking, { loading: confirming }] =
    useMutation(CONFIRM_BOOKING);

  const booking = data ? data.booking : null;
  const isConfirmed = booking?.state === "CONFIRMED";
  if (booking && booking.group.productId !== props.productId)
    return <>404: product & booking mismatch</>; // booking should belong to product

  return (
    <>
      {loading && <LinearProgress color="error" />}
      {error && <>ERROR Something went wrong</>}
      {!loading && !booking && <>404: booking not found</>}
      {!loading && booking && (
        <>
          <CheckoutProgress activeStep={1} />
          <Stack>
            <BookingInfo booking={booking} />
            {!isConfirmed && (
              <Stack className={styles.confirmationOptions}>
                <Box>
                  We will send a confirmation email to{" "}
                  <b>
                    {JSON.parse(localStorage.getItem("user") || "").userEmail}
                  </b>
                </Box>
                <FormControlLabel
                  control={
                    <Checkbox
                      size="small"
                      checked={sendConfirmationEmailToCustomer}
                      onClick={() =>
                        setSendConfirmationEmailToCustomer(
                          !sendConfirmationEmailToCustomer,
                        )
                      }
                    />
                  }
                  label={
                    <span className={styles.smallText}>
                      Also send a confirmation email to the customer (
                      <b>{booking.email}</b>) (price won't be displayed)
                    </span>
                  }
                />
                {requiresTransportAgreement(booking.group.product) && (
                  <FormControlLabel
                    control={
                      <Checkbox
                        size="small"
                        checked={isTransportAgreementAgreed}
                        onClick={() =>
                          setIsTransportAgreementAgreed(
                            !isTransportAgreementAgreed,
                          )
                        }
                      />
                    }
                    label={
                      <span className={styles.smallText}>
                        I agree to the{" "}
                        <span
                          className={styles.agreementLink}
                          onClick={(e) => {
                            e.preventDefault();
                            setTsaModalOpen(true);
                          }}
                        >
                          Transportation Service Agreement
                        </span>
                      </span>
                    }
                  />
                )}
              </Stack>
            )}
            {!isConfirmed && (
              <Button
                className={styles.confirmButton}
                variant="contained"
                disabled={
                  confirming ||
                  (requiresTransportAgreement(booking.group.product) &&
                    !isTransportAgreementAgreed)
                }
                onClick={handleConfirmBooking}
              >
                {confirming ? (
                  <CircularProgress size={24} color="inherit" />
                ) : (
                  "Confirm Booking"
                )}
              </Button>
            )}
          </Stack>
        </>
      )}
      <TransportServiceAgreementModal
        bookingId={tsaModalOpen ? props.id : null}
        onClose={() => setTsaModalOpen(false)}
      />
    </>
  );

  function handleConfirmBooking() {
    confirmBooking({
      variables: { id: props.id, sendConfirmationEmailToCustomer },
      refetchQueries: ["GetBookings"],
    }).then(
      () => {
        showNotification({
          severity: "success",
          message: "Booking has been confirmed",
        });
        navigate("/bookings");
      },
      () =>
        showNotification({
          severity: "error",
          message: "Failed to confirm booking",
        }),
    );
  }
}

function BookingInfo(props: { booking: Booking }) {
  const { booking } = props;

  const isConfirmed = booking.state === "CONFIRMED";
  const reservationDateTime = DateTime.fromFormat(
    `${booking.group.date} ${booking.group.time}`,
    "yyyy-MM-dd HH:mm",
    { zone: booking.group.product.location.timezone },
  );

  const grossPaymentAmount = getGrossPrice(booking.payment.amount);

  return (
    <>
      {isConfirmed ? (
        <Box className={styles.confirmedMessage}>
          Thank you! The booking has been confirmed
        </Box>
      ) : (
        <Alert severity="warning" className={styles.warning}>
          <AlertTitle className={styles.warningTitle}>
            The booking has not yet been confirmed!
          </AlertTitle>
          <Box className={styles.warningDescription}>
            Review the details below, then click <b>Confirm Booking</b> to
            complete your booking.
          </Box>
        </Alert>
      )}
      {!isConfirmed && (
        <Box className={styles.reviewPrompt}>
          Are you sure you want to confirm the following booking?
        </Box>
      )}
      <Box className={styles.bookingDetails}>
        <GuestNamesSummary names={booking.guests} />
        <div>
          <b>Email:</b> {booking.email}
        </div>
        {booking.phoneNumber && (
          <div>
            <b>Phone Number:</b> {booking.phoneNumber}
          </div>
        )}
        {booking.sourceBookingId && (
          <div>
            <b>Reference Id:</b> {booking.sourceBookingId}
          </div>
        )}
        {booking.specialRequests && (
          <div>
            <b>Notes:</b>{" "}
            <span className={styles.notes}>
              {booking.specialRequests}
            </span>
          </div>
        )}
      </Box>
      <Box className={styles.bookingDetails}>
        <div>
          <b>Tour:</b> {booking.group.product.translation.name}
        </div>
        {booking.group.product.availabilityType === "PRIVATE" &&
        booking.pickupLocation?.query ? (
          <div>
            <b>Pickup Location:</b>{" "}
            <a
              className={styles.pickupLink}
              href={generateGoogleMapsUrl(booking.pickupLocation?.query)}
              target="_blank"
              rel="noopener noreferrer"
            >
              {booking.pickupLocation.name}
              <br />
              {booking.pickupLocation.address}
            </a>
          </div>
        ) : booking.group.product.availabilityType === "PRIVATE" &&
          !booking.pickupLocation?.query ? (
          <div>
            <b>Pickup Location:</b> <span>Pickup not set</span>
          </div>
        ) : (
          <div>
            <b>Meeting Place:</b>{" "}
            <a
              className={styles.meetingPlaceLink}
              href={booking.group.product.meetingPlaceGoogleMapsQuery.url}
              target="_blank"
            >
              {booking.group.product.translation.meetingPlaceName}
            </a>
          </div>
        )}
        <div>
          <b>Reservation Date / Time:</b>{" "}
          {reservationDateTime.toFormat("cccc LLLL d, HH:mm (ZZZZ)")}
        </div>
        <div>
          <b>Guests:</b>{" "}
          <div>
            {booking.details.map((detail, index) => (
              <div key={index}>
                {detail.quantity} x {genProductPriceLabel(detail.productPrice)}
              </div>
            ))}
          </div>
          <b>Group:</b> {booking.isPrivate ? "private" : "public"}
        </div>
      </Box>
      <Box>
        <b>Net Price:</b>{" "}
        {Intl.NumberFormat("ja-JP", {
          style: "currency",
          currency: booking.payment.currency,
        }).format(booking.payment.amount)}
        {grossPaymentAmount && (
          <span className={styles.smallText}>
            {" "}
            (<b>Gross Price:</b>{" "}
            {Intl.NumberFormat("ja-JP", {
              style: "currency",
              currency: booking.payment.currency,
            }).format(grossPaymentAmount)}
            )
          </span>
        )}
      </Box>
    </>
  );
}

const GET_BOOKING = gql`
  query GetBooking($id: ID!, $lang: Iso639!) {
    booking(id: $id) {
      id
      isPrivate
      state
      email
      phoneNumber
      specialRequests
      sourceBookingId

      guests {
        givenName
        familyName
      }

      group {
        date
        isPrivate
        productId
        time

        product {
          location {
            timezone
          }
          translation(lang: $lang) {
            name
            meetingPlaceName
          }
          meetingPlaceGoogleMapsQuery {
            url
          }
          availabilityType
          hasAccomodationVehiclePickup
          hasDefaultVehiclePickup
          requiresTransportAgreement
        }
      }
      details {
        quantity
        productPrice {
          minAge
          maxAge
          tags
        }
      }
      pickupLocation {
        name
        address
        query {
          placeId
          geoLocation {
            lat
            lng
          }
        }
      }
      payment {
        amount
        currency
      }
    }
  }
`;

const CONFIRM_BOOKING = gql`
  mutation ConfirmBooking(
    $id: ID!
    $sendConfirmationEmailToCustomer: Boolean!
  ) {
    confirmBooking(
      input: {
        id: $id
        sendConfirmationEmailToCustomer: $sendConfirmationEmailToCustomer
      }
    )
  }
`;
